import { useState } from 'react';
import {
  Typography,
  Card,
  Button,
  Tag,
  Space,
  Table,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Drawer,
  Avatar,
  Row,
  Col,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TrophyOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  useLeagueList,
  useLeagueDetail,
  useCreateLeague,
  useUpdateLeague,
  usePublishLeague,
  useStartLeague,
  useEndLeague,
  useDeleteLeague,
} from '@/hooks/useLeagues';
import { usePinList } from '@/hooks/usePins';
import { SPORT_TYPE_CONFIG } from '@/config/constants';
import type { League, LeagueStatus, LeagueCreateRequest, LeagueUpdateRequest } from '@/types/league';
import type { SportType } from '@/types/user';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const LEAGUE_STATUS_CONFIG: Record<LeagueStatus, { label: string; color: string }> = {
  DRAFT: { label: '초안', color: 'default' },
  SCHEDULED: { label: '예정', color: 'blue' },
  ACTIVE: { label: '진행중', color: 'green' },
  ENDED: { label: '종료', color: 'orange' },
  ARCHIVED: { label: '보관', color: 'gray' },
};

// 폼 필드 타입
interface LeagueFormValues {
  name: string;
  pinId: string;
  sportType: SportType;
  dateRange: [Dayjs, Dayjs];
  startingScore?: number;
  minGamesForRank?: number;
  description?: string;
  prizeDescription?: string;
  bannerImageUrl?: string;
}

export function LeaguePage() {
  const [statusFilter, setStatusFilter] = useState<LeagueStatus | undefined>();
  const [page, setPage] = useState(1);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<League | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLeagueId, setDetailLeagueId] = useState<string>('');
  const [form] = Form.useForm<LeagueFormValues>();

  const { data, isLoading } = useLeagueList({ status: statusFilter, page, pageSize: 20 });
  const { data: detailData, isLoading: isDetailLoading } = useLeagueDetail(detailLeagueId);

  // 핀 목록 (Select 옵션용, 전체 조회)
  const { data: pinData } = usePinList({ pageSize: 200, isActive: true });

  const createMutation = useCreateLeague();
  const updateMutation = useUpdateLeague();
  const publishMutation = usePublishLeague();
  const startMutation = useStartLeague();
  const endMutation = useEndLeague();
  const deleteMutation = useDeleteLeague();

  const handleOpenCreate = () => {
    setEditTarget(null);
    form.resetFields();
    setFormModalOpen(true);
  };

  const handleOpenEdit = (record: League) => {
    setEditTarget(record);
    form.setFieldsValue({
      name: record.name,
      pinId: record.pinId,
      sportType: record.sportType,
      dateRange: [dayjs(record.startsAt), dayjs(record.endsAt)],
    });
    setFormModalOpen(true);
  };

  const handleCloseModal = () => {
    setFormModalOpen(false);
    setEditTarget(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [startsAt, endsAt] = values.dateRange;

      if (editTarget) {
        const updateData: LeagueUpdateRequest = {
          name: values.name,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          startingScore: values.startingScore,
          minGamesForRank: values.minGamesForRank,
          description: values.description,
          prizeDescription: values.prizeDescription,
          bannerImageUrl: values.bannerImageUrl,
        };
        updateMutation.mutate({ id: editTarget.id, data: updateData }, { onSuccess: handleCloseModal });
      } else {
        const createData: LeagueCreateRequest = {
          name: values.name,
          pinId: values.pinId,
          sportType: values.sportType,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          startingScore: values.startingScore,
          minGamesForRank: values.minGamesForRank,
          description: values.description,
          prizeDescription: values.prizeDescription,
          bannerImageUrl: values.bannerImageUrl,
        };
        createMutation.mutate(createData, { onSuccess: handleCloseModal });
      }
    } catch {
      // 유효성 검증 실패 — form이 에러를 표시함
    }
  };

  const handleOpenDetail = (id: string) => {
    setDetailLeagueId(id);
    setDetailDrawerOpen(true);
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  const columns: TableColumnsType<League> = [
    {
      title: '리그명',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: League) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 500 }}
          onClick={() => handleOpenDetail(record.id)}
        >
          {name}
        </Button>
      ),
    },
    {
      title: '핀',
      dataIndex: 'pinName',
      key: 'pinName',
      width: 120,
    },
    {
      title: '종목',
      dataIndex: 'sportType',
      key: 'sportType',
      render: (sportType: SportType) => (
        <Tag>{SPORT_TYPE_CONFIG[sportType]?.label ?? sportType}</Tag>
      ),
      width: 100,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: LeagueStatus) => (
        <Tag color={LEAGUE_STATUS_CONFIG[status]?.color}>
          {LEAGUE_STATUS_CONFIG[status]?.label ?? status}
        </Tag>
      ),
      width: 90,
    },
    {
      title: '기간',
      key: 'period',
      render: (_, record: League) => (
        <Text style={{ fontSize: 12 }}>
          {dayjs(record.startsAt).format('YYYY-MM-DD')} ~ {dayjs(record.endsAt).format('YYYY-MM-DD')}
        </Text>
      ),
      width: 200,
    },
    {
      title: '참가자',
      dataIndex: 'participantCount',
      key: 'participantCount',
      render: (v: number) => `${(v ?? 0).toLocaleString()}명`,
      width: 80,
    },
    {
      title: '우승자',
      dataIndex: 'winnerUserId',
      key: 'winnerUserId',
      render: (winnerUserId: string | null) =>
        winnerUserId ? <Tag color="gold"><TrophyOutlined /> 확정</Tag> : <Text type="secondary">-</Text>,
      width: 90,
    },
    {
      title: '액션',
      key: 'actions',
      render: (_, record: League) => (
        <Space size={4}>
          {record.status === 'DRAFT' && (
            <>
              <Popconfirm
                title="리그를 공개(예정)로 변경하시겠습니까?"
                okText="공개"
                cancelText="취소"
                onConfirm={() => publishMutation.mutate(record.id)}
              >
                <Button size="small" type="primary">
                  공개
                </Button>
              </Popconfirm>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>
                수정
              </Button>
              <Popconfirm
                title="리그를 삭제하시겠습니까?"
                okText="삭제"
                cancelText="취소"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteMutation.mutate(record.id)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
          {record.status === 'SCHEDULED' && (
            <>
              <Popconfirm
                title="리그를 지금 시작하시겠습니까?"
                okText="시작"
                cancelText="취소"
                onConfirm={() => startMutation.mutate(record.id)}
              >
                <Button size="small" type="primary">
                  시작
                </Button>
              </Popconfirm>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>
                수정
              </Button>
            </>
          )}
          {record.status === 'ACTIVE' && (
            <Popconfirm
              title="리그를 종료하시겠습니까? 우승자가 확정됩니다."
              okText="종료"
              cancelText="취소"
              okButtonProps={{ danger: true }}
              onConfirm={() => endMutation.mutate(record.id)}
            >
              <Button size="small" danger>
                종료
              </Button>
            </Popconfirm>
          )}
          {(record.status === 'ENDED' || record.status === 'ARCHIVED') && (
            <Button size="small" onClick={() => handleOpenDetail(record.id)}>
              상세
            </Button>
          )}
        </Space>
      ),
      width: 180,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            리그/대회 관리
          </Title>
          <Text type="secondary">리그 생성, 상태 전환 및 순위 조회</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          리그 생성
        </Button>
      </div>

      {/* 상태 필터 */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Space>
          <Text>상태:</Text>
          <Select
            placeholder="전체"
            style={{ width: 130 }}
            value={statusFilter ?? ''}
            onChange={(v) => {
              setStatusFilter(v === '' ? undefined : (v as LeagueStatus));
              setPage(1);
            }}
            options={[
              { value: '', label: '전체' },
              ...Object.entries(LEAGUE_STATUS_CONFIG).map(([value, { label }]) => ({
                value,
                label,
              })),
            ]}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 8 }}>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total || 0,
            showTotal: (total) => `총 ${(total ?? 0).toLocaleString()}개`,
            onChange: setPage,
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* 생성/수정 모달 */}
      <Modal
        open={formModalOpen}
        title={editTarget ? '리그 수정' : '리그 생성'}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        okText="저장"
        cancelText="취소"
        confirmLoading={isMutating}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="리그명"
            name="name"
            rules={[{ required: true, message: '리그명을 입력해주세요.' }]}
          >
            <Input placeholder="리그 이름" maxLength={100} showCount />
          </Form.Item>

          {/* 핀 선택: 생성 시에만 표시 (수정 시 변경 불가) */}
          {!editTarget && (
            <Form.Item
              label="핀"
              name="pinId"
              rules={[{ required: true, message: '핀을 선택해주세요.' }]}
            >
              <Select
                placeholder="핀 선택"
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={(pinData?.items || []).map((pin) => ({
                  value: pin.id,
                  label: pin.name,
                }))}
              />
            </Form.Item>
          )}

          {/* 종목 선택: 생성 시에만 표시 */}
          {!editTarget && (
            <Form.Item
              label="종목"
              name="sportType"
              rules={[{ required: true, message: '종목을 선택해주세요.' }]}
            >
              <Select
                placeholder="종목 선택"
                options={Object.entries(SPORT_TYPE_CONFIG).map(([value, { label }]) => ({
                  value,
                  label,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            label="기간"
            name="dateRange"
            rules={[{ required: true, message: '기간을 선택해주세요.' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="시작 점수" name="startingScore">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="기본값 사용"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="순위 집계 최소 경기 수" name="minGamesForRank">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="기본값 사용"
                  min={1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="배너 이미지 URL" name="bannerImageUrl">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item label="상품/혜택 설명" name="prizeDescription">
            <Input.TextArea rows={2} placeholder="우승자 혜택 등" />
          </Form.Item>

          <Form.Item label="리그 설명" name="description">
            <Input.TextArea rows={3} placeholder="리그 상세 설명" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 상세/순위표 Drawer */}
      <Drawer
        title="리그 상세 / 순위표"
        placement="right"
        width={600}
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setDetailLeagueId('');
        }}
      >
        {isDetailLoading ? (
          <Text type="secondary">로딩 중...</Text>
        ) : detailData ? (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">리그명</Text>
                  <div>
                    <Text strong>{detailData.name}</Text>
                    <Tag style={{ marginLeft: 8 }} color={LEAGUE_STATUS_CONFIG[detailData.status]?.color}>
                      {LEAGUE_STATUS_CONFIG[detailData.status]?.label}
                    </Tag>
                  </div>
                </div>
                <div>
                  <Text type="secondary">핀 / 종목</Text>
                  <div>
                    <Text>{detailData.pinName}</Text>
                    <Text type="secondary"> / </Text>
                    <Text>{SPORT_TYPE_CONFIG[detailData.sportType]?.label ?? detailData.sportType}</Text>
                  </div>
                </div>
                <div>
                  <Text type="secondary">기간</Text>
                  <div>
                    <Text>
                      {dayjs(detailData.startsAt).format('YYYY-MM-DD HH:mm')} ~{' '}
                      {dayjs(detailData.endsAt).format('YYYY-MM-DD HH:mm')}
                    </Text>
                  </div>
                </div>
                {detailData.prizeDescription && (
                  <div>
                    <Text type="secondary">상품/혜택</Text>
                    <div><Text>{detailData.prizeDescription}</Text></div>
                  </div>
                )}
                {detailData.description && (
                  <div>
                    <Text type="secondary">설명</Text>
                    <div><Text>{detailData.description}</Text></div>
                  </div>
                )}
              </Space>
            </Card>

            <Title level={5}>순위표 ({detailData.participantCount}명)</Title>
            <Table
              dataSource={detailData.rankings}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '순위',
                  dataIndex: 'rank',
                  key: 'rank',
                  width: 55,
                  render: (rank: number) =>
                    rank === 1 ? (
                      <Tag color="gold"><TrophyOutlined /> {rank}</Tag>
                    ) : (
                      <Text>{rank}</Text>
                    ),
                },
                {
                  title: '닉네임',
                  key: 'nickname',
                  render: (_, row) => (
                    <Space>
                      <Avatar size="small" src={row.profileImageUrl ?? undefined} />
                      <Text>{row.nickname}</Text>
                    </Space>
                  ),
                },
                {
                  title: '점수',
                  dataIndex: 'score',
                  key: 'score',
                  width: 70,
                  render: (v: number) => v.toFixed(0),
                },
                {
                  title: '경기',
                  key: 'record',
                  width: 100,
                  render: (_, row) => (
                    <Text style={{ fontSize: 12 }}>
                      {row.gamesPlayed}전 {row.wins}승 {row.losses}패 {row.draws}무
                    </Text>
                  ),
                },
              ]}
              scroll={{ y: 400 }}
            />
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
